// API helpers para el sistema de modelos guardados de Interfaz V2.
//
// Tabla `modelos`:
//   id (uuid)  ·  created_at (timestamptz — orden de creación)
//   id_company (uuid, nullable — NULL = modelo global de Atlas)
//   nombre (text — display name)
//   foto_file_name (text — filename del thumbnail / presentación)
//   fotos_referencia (text[] — filenames de fotos internas multi-ángulo)
//
// Storage bucket `modelos`:
//   • Modelos privados: `<idCompany>/full/<fileName>`
//   • Modelos globales:  `global/full/<fileName>`
// Las URLs siempre se firman (TTL 1h) — el bucket NO es público.

import { supabase } from '../../lib/supabase';
import { GENERATE_AUTH_TOKEN, USE_ATLAS_BACKEND } from '../../config';
import { atlasBackendFetch } from '../../lib/atlasBackend';

const BUCKET = 'modelos';
const SIGNED_TTL = 3600;
// Creación de modelo desde características (modo 'crear') / desde foto
// (`imagenes`). Con flag atlas ON solo queda como fallback n8n del modo
// características y como ÚNICO camino del modo foto (ver createModelFromPhoto).
const PROCESAR_MODELO_CREADO_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/procesar_modelo_creado';

export interface ModelV2 {
  id: number;
  nombre: string;
  /** null = global de Atlas, uuid = privado de esa empresa. */
  idCompany: string | null;
  /** Filename del thumbnail/presentación (la que se ve en el picker). */
  fotoFileName: string | null;
  /** Filenames de las fotos de referencia (multi-ángulo, uso interno). */
  fotosReferencia: string[];
  /** Signed URL del thumbnail — ya resuelto. FULL RES: el VideoEditor lo
   *  baja y lo sube como asset del proyecto, no achicar. */
  thumbnailUrl: string;
  /** Signed URL en miniatura (transformación de Supabase) SOLO para display
   *  en carruseles/pickers. Las fotos de presentación son PNGs grandes
   *  ("angles"): decodificar todos los modelos globales de una reventaba el
   *  compositor (glitches de UI — mismo episodio que los fondos 2048×2048).
   *  Fallback: si la transformación falla, cae a `thumbnailUrl`. */
  thumbUrl: string;
  /** Signed URLs de fotos_referencia — ya resueltas, en mismo orden. */
  referenceUrls: string[];
}

/**
 * Devuelve el path en el bucket `modelos` para un filename de ese modelo
 * según si es global (vive en `global/full/`) o privado (`<company>/full/`).
 */
export function getModelStoragePath(
  model: Pick<ModelV2, 'idCompany'>,
  fileName: string,
): string {
  if (model.idCompany === null) {
    return `global/full/${fileName}`;
  }
  return `${model.idCompany}/full/${fileName}`;
}

async function signFile(path: string): Promise<string> {
  const { data } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, SIGNED_TTL);
  return data?.signedUrl || '';
}

// Caja 440×782 (tiles 9/16 de 140–220px @2x) con `contain`: preserva el
// aspecto y el object-cover del CSS hace el mismo crop visual que antes.
async function signFileThumb(path: string): Promise<string> {
  const { data } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, SIGNED_TTL, {
      transform: { width: 440, height: 782, resize: 'contain', quality: 75 },
    });
  return data?.signedUrl || '';
}

/**
 * Fetcha todos los modelos visibles para el user: globales + los privados
 * de su empresa. Firma todas las URLs (thumbnail + referencias) y las
 * devuelve listas para usar. Globales primero (NULLS FIRST), luego por nombre.
 */
export async function fetchUserModels(
  companyId: string,
): Promise<ModelV2[]> {
  const { data, error } = await supabase
    .from('modelos')
    .select('id, nombre, id_company, foto_file_name, fotos_referencia')
    .or(`id_company.is.null,id_company.eq.${companyId}`)
    // Más recientes primero por fecha de creación. OJO: `id` es UUID (no
    // autoincrement), así que NO sirve para ordenar — hay que usar created_at.
    // Este orden se propaga a todos los menús (sort estable downstream).
    .order('created_at', { ascending: false });
  if (error || !data) {
    console.error('[modelsApi] fetchUserModels error', error);
    return [];
  }

  // Firmar URLs en paralelo
  const models: ModelV2[] = await Promise.all(
    (data as any[]).map(async (m): Promise<ModelV2> => {
      const partial: Pick<ModelV2, 'idCompany'> = {
        idCompany: m.id_company || null,
      };
      const fotoFileName: string | null = m.foto_file_name || null;
      const fotosReferencia: string[] = Array.isArray(m.fotos_referencia)
        ? m.fotos_referencia
        : [];
      const fotoPath = fotoFileName
        ? getModelStoragePath(partial, fotoFileName)
        : null;
      const [thumbnailUrl, thumbUrl] = fotoPath
        ? await Promise.all([signFile(fotoPath), signFileThumb(fotoPath)])
        : ['', ''];
      const referenceUrls = await Promise.all(
        fotosReferencia.map((fn) =>
          signFile(getModelStoragePath(partial, fn)),
        ),
      );
      return {
        id: m.id as number,
        nombre: (m.nombre as string) || 'Modelo',
        idCompany: m.id_company || null,
        fotoFileName,
        fotosReferencia,
        thumbnailUrl,
        thumbUrl: thumbUrl || thumbnailUrl,
        referenceUrls,
      };
    }),
  );
  return models;
}

/** Fetcha un modelo puntual por id (para el flow post-procesamiento). */
export async function fetchModelById(
  modelId: number,
): Promise<ModelV2 | null> {
  const { data, error } = await supabase
    .from('modelos')
    .select('id, nombre, id_company, foto_file_name, fotos_referencia')
    .eq('id', modelId)
    .single();
  if (error || !data) {
    console.error('[modelsApi] fetchModelById error', error);
    return null;
  }
  const m = data as any;
  const partial: Pick<ModelV2, 'idCompany'> = {
    idCompany: m.id_company || null,
  };
  const fotoFileName: string | null = m.foto_file_name || null;
  const fotosReferencia: string[] = Array.isArray(m.fotos_referencia)
    ? m.fotos_referencia
    : [];
  const fotoPath = fotoFileName
    ? getModelStoragePath(partial, fotoFileName)
    : null;
  const [thumbnailUrl, thumbUrl] = fotoPath
    ? await Promise.all([signFile(fotoPath), signFileThumb(fotoPath)])
    : ['', ''];
  const referenceUrls = await Promise.all(
    fotosReferencia.map((fn) => signFile(getModelStoragePath(partial, fn))),
  );
  return {
    id: m.id as number,
    nombre: (m.nombre as string) || 'Modelo',
    idCompany: m.id_company || null,
    fotoFileName,
    fotosReferencia,
    thumbnailUrl,
    thumbUrl: thumbUrl || thumbnailUrl,
    referenceUrls,
  };
}

/** Actualiza solo el nombre de un modelo. */
export async function updateModelName(
  modelId: number,
  nombre: string,
): Promise<void> {
  const { error } = await supabase
    .from('modelos')
    .update({ nombre })
    .eq('id', modelId);
  if (error) throw error;
}

/**
 * Elimina un modelo privado: borra sus archivos del storage (thumbnail + fotos
 * de referencia) y la fila en `modelos`. Los modelos globales de Atlas
 * (idCompany === null) NO se pueden eliminar.
 */
export async function deleteModel(
  model: Pick<ModelV2, 'id' | 'idCompany' | 'fotoFileName' | 'fotosReferencia'>,
): Promise<void> {
  if (model.idCompany === null) {
    throw new Error('No se pueden eliminar los modelos globales de Atlas.');
  }
  const paths: string[] = [];
  if (model.fotoFileName) {
    paths.push(getModelStoragePath(model, model.fotoFileName));
  }
  for (const fn of model.fotosReferencia) {
    paths.push(getModelStoragePath(model, fn));
  }
  // Storage best-effort: aunque falle (o no exista), igual borramos la fila.
  if (paths.length > 0) {
    await supabase.storage.from(BUCKET).remove(paths);
  }
  const { error } = await supabase.from('modelos').delete().eq('id', model.id);
  if (error) throw error;
}

// ─── File → base64 (sin el prefix `data:...,`) ────────────────────────
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip "data:image/jpeg;base64," etc — el backend espera solo base64.
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.readAsDataURL(file);
  });
}

// El backend puede devolver el b64 con o sin el prefix `data:image/...;base64,`.
// Lo normalizamos a sin prefix para subirlo al storage como blob.
function stripB64Prefix(raw: string): string {
  const idx = raw.indexOf(',');
  return idx >= 0 ? raw.slice(idx + 1) : raw;
}

/**
 * Valor del tipo de cuerpo "extra grande" — el string que viaja al backend y la
 * etiqueta que muestra `ModelPicker`. Una sola fuente para que la opción del
 * menú y lo que el backend matchea no se desincronicen.
 *
 * El refuerzo de prompt que vivía acá (`TIPO_CUERPO_EXTRA_GRANDE_PROMPT`, una
 * aclaración apendeada al valor pidiendo sobrepeso evidente) se ELIMINÓ: ahora
 * vive en el backend (`services/modelos/prompts.ts`), donde además alcanza a la
 * 2ª generación —la foto de cuerpo entero que queda como el modelo, que antes no
 * recibía ninguna característica— y aplica desde CUALQUIER pantalla, no solo
 * desde esta. Ver ADR-2026-08-06-talle-extra-grande-refuerzo-en-el-backend.
 */
export const TIPO_CUERPO_EXTRA_GRANDE = 'Talle Extra Grande';

/** Características del modelo personalizado (espejo de `CustomModelFields`). */
export interface CreateModelFields {
  genero?: string;
  edad?: string;
  tipoCuerpo?: string;
  tonoPiel?: string;
  colorPelo?: string;
  largoPelo?: string;
  descripcion?: string;
}

/**
 * Crea un modelo desde cero a partir de las características elegidas por el
 * user (sin fotos). El backend genera y devuelve dos b64 (`image_ref` +
 * `image_angles_character`), por lo que el guardado (`saveProcessedModel`)
 * se reutiliza tal cual.
 *
 * Flag atlas ON → `/v1/models/create` con JWT (gratis; sin saldo = 402 con
 * error_message, antes n8n colgaba 300s). OFF → webhook n8n byte-idéntico.
 */
export async function createModelFromFields({
  userId,
  fields,
}: {
  userId: string;
  fields: CreateModelFields;
}): Promise<{ imageRefB64: string; imageAnglesB64: string }> {
  const caracteristicas: Record<string, string> = {};
  if (fields.genero) caracteristicas.genero = fields.genero;
  if (fields.edad) caracteristicas.edad = fields.edad;
  if (fields.tipoCuerpo) caracteristicas.tipo_cuerpo = fields.tipoCuerpo;
  if (fields.tonoPiel) caracteristicas.tono_piel = fields.tonoPiel;
  if (fields.colorPelo) caracteristicas.color_pelo = fields.colorPelo;
  if (fields.largoPelo) caracteristicas.largo_pelo = fields.largoPelo;
  if (fields.descripcion) caracteristicas.descripcion = fields.descripcion;

  let response: Response;
  if (USE_ATLAS_BACKEND) {
    // user_id NO viaja (sale del JWT) y `modo` tampoco hace falta: el
    // endpoint solo crea desde características.
    response = await atlasBackendFetch('/v1/models/create', { caracteristicas });
  } else {
    response = await fetch(PROCESAR_MODELO_CREADO_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify({ user_id: userId, modo: 'crear', caracteristicas }),
    });
  }
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    // El atlas responde JSON tipado ({error_message}) — mostrarlo tal cual;
    // n8n devuelve texto crudo y el JSON.parse falla → queda el texto.
    let msg = txt;
    try {
      msg = (JSON.parse(txt) as { error_message?: string })?.error_message || txt;
    } catch { /* texto crudo de n8n */ }
    throw new Error(msg || `Error ${response.status} creando modelo`);
  }
  const data: any = await response.json();
  if (
    data?.response !== 'success' ||
    typeof data?.image_ref !== 'string' ||
    typeof data?.image_angles_character !== 'string'
  ) {
    throw new Error('Respuesta inesperada del servidor');
  }
  return {
    imageRefB64: stripB64Prefix(data.image_ref as string),
    imageAnglesB64: stripB64Prefix(data.image_angles_character as string),
  };
}

// ─── Save: cliente guarda en storage + DB ──────────────────────────────
// b64 → Blob para subir al bucket.
function b64ToBlob(b64: string, type = 'image/png'): Blob {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type });
}

// Slug + timestamp para filenames únicos.
function buildModelFileName(seed: string, ext: string): string {
  const slug = seed
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 40);
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${
    slug || 'modelo'
  }.${ext}`;
}

/**
 * Persiste el modelo procesado. Los dos b64 vienen del backend (ver
 * `createModelFromFields` / `createModelFromPhoto`):
 *   1. Sube `presentationB64` (image_ref) → `modelos/<company>/full/<fileName>.png`
 *      → queda como `foto_file_name` (thumbnail visible en el picker).
 *   2. Sube `anglesB64` (image_angles_character) al mismo path → entrada
 *      única en `fotos_referencia[]` (uso interno multi-ángulo).
 *   3. INSERT en `modelos`.
 *   4. Devuelve el `ModelV2` recién creado con signed URLs resueltas.
 *
 * Se llama cuando el user toca "Utilizar" en la UI.
 */
export async function saveProcessedModel({
  companyId,
  name,
  presentationB64,
  anglesB64,
  tipoCuerpo,
}: {
  companyId: string;
  name: string;
  presentationB64: string;
  anglesB64: string;
  /** Chip elegido al crear (`Talle Extra Grande`, …). Se persiste para que la
   *  GENERACIÓN pueda afirmar la contextura: sin esto el dato se perdía al
   *  guardar. Ausente en los flujos donde el user no eligió (foto propia). */
  tipoCuerpo?: string;
}): Promise<ModelV2> {
  // Nombre placeholder si el user no escribió ninguno — "Modelo N+1".
  let effectiveName = name.trim();
  if (!effectiveName) {
    const { count } = await supabase
      .from('modelos')
      .select('id', { count: 'exact', head: true })
      .eq('id_company', companyId);
    effectiveName = `Modelo ${(count ?? 0) + 1}`;
  }

  // 1. Subir presentación (image_ref → PNG blob)
  const presentationBlob = b64ToBlob(presentationB64, 'image/png');
  const presentationName = buildModelFileName(effectiveName, 'png');
  const presentationPath = `${companyId}/full/${presentationName}`;
  const { error: pErr } = await supabase.storage
    .from(BUCKET)
    .upload(presentationPath, presentationBlob, {
      cacheControl: '3600',
      upsert: false,
      contentType: 'image/png',
    });
  if (pErr) throw pErr;

  // 2. Subir image_angles_character (PNG blob) — una sola entrada en
  //    fotos_referencia[], aunque la imagen contenga múltiples ángulos.
  const anglesBlob = b64ToBlob(anglesB64, 'image/png');
  const anglesName = buildModelFileName(`${effectiveName}-angles`, 'png');
  const anglesPath = `${companyId}/full/${anglesName}`;
  const { error: aErr } = await supabase.storage
    .from(BUCKET)
    .upload(anglesPath, anglesBlob, {
      cacheControl: '3600',
      upsert: false,
      contentType: 'image/png',
    });
  if (aErr) throw aErr;

  // 3. INSERT en modelos
  const { data: inserted, error: insErr } = await supabase
    .from('modelos')
    .insert({
      id_company: companyId,
      nombre: effectiveName,
      foto_file_name: presentationName,
      fotos_referencia: [anglesName],
      tipo_cuerpo: tipoCuerpo || null,
    })
    .select('id')
    .single();
  if (insErr) throw insErr;

  // 4. Fetch + return el modelo completo (signed URLs incluidas)
  const created = await fetchModelById((inserted as any).id as number);
  if (!created) {
    throw new Error('Modelo guardado pero no se pudo cargar de vuelta');
  }
  return created;
}

/**
 * Crea un modelo desde una FOTO subida por el user, procesándola a FONDO BLANCO.
 * Manda la foto a `procesar_modelo_creado` (SINCRÓNICO) — el webhook que
 * reemplazó al viejo `procesar_modelo_propio` (borrado) — y espera la respuesta
 * con el modelo procesado (`image_ref` = presentación con fondo blanco,
 * `image_angles_character` = hoja de ángulos), que luego guarda con
 * `saveProcessedModel`. A diferencia de `uploadOwnModelDirect` (foto cruda,
 * fire-and-forget), acá el modelo queda prolijo con fondo blanco.
 *
 * Usado por el "+" del picker de personajes del editor de video. NO cambia el
 * flujo de la Interfaz V2 (que sigue con `uploadOwnModelDirect`).
 *
 * OJO: sigue en n8n AUN con flag atlas ON — el `/v1/models/create` del atlas
 * solo acepta `caracteristicas` (su zod descartaría `imagenes` en silencio y
 * generaría un modelo inventado ignorando la foto). Migrar cuando el backend
 * soporte el modo foto.
 */
export async function createModelFromPhoto({
  companyId,
  userId,
  name,
  file,
}: {
  companyId: string;
  userId: string;
  name: string;
  file: File;
}): Promise<ModelV2> {
  const imagenes = [await fileToBase64(file)];
  const response = await fetch(PROCESAR_MODELO_CREADO_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: GENERATE_AUTH_TOKEN,
    },
    // Sin "inventar": mandamos la foto en `imagenes` + `caracteristicas` vacío.
    body: JSON.stringify({ user_id: userId, imagenes, caracteristicas: {} }),
  });
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    throw new Error(txt || `Error ${response.status} procesando el personaje`);
  }
  const data: any = await response.json();
  if (
    data?.response !== 'success' ||
    typeof data?.image_ref !== 'string' ||
    typeof data?.image_angles_character !== 'string'
  ) {
    throw new Error(data?.error || 'Respuesta inesperada del servidor');
  }
  return saveProcessedModel({
    companyId,
    name,
    presentationB64: stripB64Prefix(data.image_ref as string),
    anglesB64: stripB64Prefix(data.image_angles_character as string),
  });
}

/**
 * Upload de modelo propio "directo" (Interfaz V2): guarda la imagen CRUDA que
 * subió el user como thumbnail + inserta la fila en `modelos`. NO hay
 * procesamiento posterior: el webhook `procesar_modelo_propio` que se
 * disparaba acá fire-and-forget se ELIMINÓ (decisión de producto — el
 * procesamiento de referencias bajaba el parecido facial; el workflow de n8n
 * ya estaba apagado y la llamada no hacía nada).
 */
export async function uploadOwnModelDirect({
  companyId,
  name,
  file,
}: {
  companyId: string;
  /** Ya no se usa (era para el webhook de procesamiento, eliminado). Se
   *  mantiene en la firma para no tocar a los callers. */
  userId?: string;
  name: string;
  file: File;
}): Promise<ModelV2> {
  // Nombre placeholder si el user no escribió ninguno — "Modelo N+1".
  let effectiveName = name.trim();
  if (!effectiveName) {
    const { count } = await supabase
      .from('modelos')
      .select('id', { count: 'exact', head: true })
      .eq('id_company', companyId);
    effectiveName = `Modelo ${(count ?? 0) + 1}`;
  }

  // 1. Subir la imagen cruda → modelos/<company>/full/<fileName>
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const fileName = buildModelFileName(effectiveName, ext);
  const fullPath = `${companyId}/full/${fileName}`;
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(fullPath, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || 'image/jpeg',
    });
  if (upErr) throw upErr;

  // 2. INSERT en modelos (solo thumbnail; sin fotos_referencia — no hay
  //    procesamiento posterior).
  const { data: inserted, error: insErr } = await supabase
    .from('modelos')
    .insert({
      id_company: companyId,
      nombre: effectiveName,
      foto_file_name: fileName,
    })
    .select('id')
    .single();
  if (insErr) throw insErr;
  const modeloId = (inserted as any).id;

  // 3. Fetch + return el modelo recién creado (signed URL del thumbnail).
  const created = await fetchModelById(modeloId as number);
  if (!created) {
    throw new Error('Modelo guardado pero no se pudo cargar de vuelta');
  }
  return created;
}
